
import { BrowserModule } from '@angular/platform-browser';
import { PreloadAllModules, RouteReuseStrategy, RouterModule, Routes } from '@angular/router';
import { NgModule, ErrorHandler, LOCALE_ID, Inject, inject, importProvidersFrom, forwardRef } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AppComponent } from './app.component';
import { DynamicFormsModule } from './dynamic-forms/dynamic-forms.module';
import { SystemViewModule } from './system-view/system-view.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import { GlobalErrorHandler } from './services/GloabalErrorHandler.services';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { AuthenticationFormComponent, AuthModule } from './system-utils/authentication-form/authentication-form.component';
import { AuthGuard } from './services/auth.guard.service';
import { ProdutosComponent } from './forms/produtos/produtos.component';
import { CriacaoProdutoFormComponent } from './forms/produtos/amazon/criacao-produto/criacao-produto-form/criacao-produto-form.component';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { LogAplicacaoComponent } from './forms/log-aplicacao/log-aplicacao.component';
import { MainDashboardComponent } from './system-view/main-dashboard/main-dashboard.component';
import { PushPedidosComponent } from './forms/push-pedidos/push-pedidos.component';
import { FormulaTesterComponent } from './forms/formula-tester/formula-tester.component';
import { FormulaLabComponent } from './forms/formula-lab/formula-lab.component';
import { SimpleDatagridComponent } from './forms/simple-datagrid/simple-datagrid.component';
import { NewProductAmazonComponent } from './forms/produtos/amazon/new-product-amazon/new-product-amazon.component';
import { MeliMlbDownloadComponent } from './forms/produtos/mercadolivre/meli-mlb-download/meli-mlb-download.component';
import { ExternalAuthenticationComponent } from './forms/authentication/authentication.component';
import { IConfig, NgxMaskModule, NgxMaskPipe } from 'ngx-mask';
import { SystemComponent } from './forms/system/system.component';
import { ChangePasswordFormComponent } from './system-utils/change-password-form/change-password-form.component';
import { AnunciosComponent } from './forms/anuncios/anuncios.component';
import { ImportacaoDadosComponent } from './forms/importacao-dados/importacao-dados.component';
import { AcessViewComponent } from './forms/acess-view/acess-view.component';
import { QueryExecutorComponent } from './forms/query-executor/query-executor.component';
import { CacheRouteReuseStrategy } from './system-utils/utils/cache-route-reuse.strategy';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MenuCreatorComponent } from './forms/menu-creator/menu-creator.component';
import { SharedModule } from './shared-module/shared-module.module';
import { WebhookCreatorComponent } from './webhook-creator/webhook-creator/webhook-creator.component';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { FlowModalComponent } from './forms/flow-action/flowaction.component';
import { EMPTY, Observable } from 'rxjs';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { HTTPService } from './services/http.service';
import { PrintScreenAlertComponent } from './system-utils/print-screen-alert/print-screen-alert.component';
import { TranslateFormComponent } from './forms/translate-form/translate-form.component';
import { DynamicLoaderService } from './services/dynamic-loader.service';
import { ConfigJsonComponent } from './forms/json-component/config-json/config-json.component';
import { JsonImporterComponent } from './forms/json-importer/json-importer.component';
import { JsonEditorModule } from './json-editor-module/json-editor.module';
import { PrivacyComponent } from './forms/privacy/privacy.component';
import { BuilderScreenBetaModule } from './builder-screen-beta/builder-screen.module';
import { BuilderScreenBetaComponent } from './builder-screen-beta/builder-screen.component';
import { TiktokAttributeComponent } from './forms/produtos/tiktok/tiktok-attribute/tiktok-attribute.component';
import { GenericLogComponent } from './forms/generic-log/generic-log.component';
import { WebhookLogComponent } from './webhook-creator/webhook-creator/webhook-log/webhook-log.component';
import { AgentBuilderConfigComponent } from './agent-builder/agent-builder-config/agent-builder-config.component';
import { AgentBuilderModule } from './agent-builder/agent-builder.module';
import { AgentBuilderListComponent } from './agent-builder/agent-builder-list/agent-builder-list.component';
import { QuillModule } from 'ngx-quill';
import { SystemViewNewComponent } from './system-view/system-view-new/system-view-new.component';
import { SystemViewLayoutSelectorComponent } from './system-view/system-view-layout-selector.component';
import { GlobalIconeFallBackService } from './services/GlobalIconeFallBackService';
import { NZ_ICONS, NZ_ICONS_PATCH, NzIconService } from 'ng-zorro-antd/icon';
import { ConciliadoraModule } from './conciliadora/conciliadora.module';
import { ConnectionBarComponent } from './app-lost-connection-bar.component';
import { ConnectionLostBarService } from './services/ConnectionLostBarService';
import { LanguageServices } from './services/LanguageServices';
import { ShopeeAttributeComponent } from './forms/produtos/shopee/shopee-attribute/shopee-attribute.component';




/**
 * Loader de traducoes com TRES camadas, na ordem de precedencia:
 *
 *  1. assets/i18n/<lang>.json  — dicionario do BUNDLE. Garante rotulos
 *     corretos nas telas ANTERIORES ao login (ex.: /changepassword com
 *     senha expirada), quando nao existe token para consultar o banco.
 *  2. localStorage             — ultima carga do banco neste navegador.
 *     Cobre 100% das telas pre-login a partir do 2o acesso.
 *  3. MKTPLANGUAGE (banco)     — autoritativo; sobrepoe 1 e 2 e renova o
 *     cache. So e consultado quando existe sessao.
 *
 * Antes este loader chamava o banco direto com authentication=true: sem
 * usuario no localStorage o HTTPService LANCAVA excecao, o dicionario
 * ficava vazio e as telas pre-login exibiam as proprias chaves.
 */
export class CustomTranslateLoader implements TranslateLoader {
  private httpService:HTTPService = inject(HTTPService);

  private static cacheKey(lang:string):string { return 'mktp.translations.' + lang; }

  constructor(private http:HttpClient){
  }

  public getTranslation(languageSelected: string): Observable<any> {

    return new Observable(observer => {

      this.loadFromAssets(languageSelected).then(staticDict => {

        let dictionary = Object.assign({}, staticDict, this.loadFromCache(languageSelected));

        if ( !this.hasSession() ){
          observer.next(dictionary);
          observer.complete();
          return;
        }

        let obinfo = {
          "entityName":"Mktplanguage",
          "sql":`SELECT * FROM MKTPLANGUAGE`
        }

        try{
          this.httpService.post(obinfo,'JsonBrokerServices/findnative',false,true,true).subscribe(val=>{
            let fromDb = this.parseRecords(val['SQL'] as Object[], languageSelected);
            this.saveCache(languageSelected, fromDb);
            observer.next(Object.assign(dictionary, fromDb));
            observer.complete();
          },error=>{
            // sem rede ou sessao invalida: segue com bundle + cache
            observer.next(dictionary);
            observer.complete();
          });
        }catch(Exception){
          observer.next(dictionary);
          observer.complete();
        }
      });
    });
  }

  /** ha sessao valida para consultar o banco? */
  private hasSession():boolean{
    try{
      let user = localStorage.getItem('user');
      return user != null && JSON.parse(user)['token'] != null;
    }catch(Exception){
      return false;
    }
  }

  private loadFromAssets(lang:string):Promise<Object>{
    return new Promise(resolve=>{
      this.http.get(`assets/i18n/${lang}.json`).subscribe(
        data => resolve(this.upperKeys(data)),
        error => resolve({})
      );
    });
  }

  private loadFromCache(lang:string):Object{
    try{
      let raw = localStorage.getItem(CustomTranslateLoader.cacheKey(lang));
      return raw != null ? JSON.parse(raw) : {};
    }catch(Exception){
      return {};
    }
  }

  private saveCache(lang:string, dictionary:Object):void{
    try{
      localStorage.setItem(CustomTranslateLoader.cacheKey(lang), JSON.stringify(dictionary));
    }catch(Exception){
      // cota cheia: o cache e apenas otimizacao, o banco continua a fonte
    }
  }

  /** o pipe translateTA busca sempre em MAIUSCULAS */
  private upperKeys(dictionary:Object):Object{
    let out = {};
    Object.keys(dictionary || {}).forEach(k => out[k.toUpperCase()] = dictionary[k]);
    return out;
  }

  private parseRecords(sqlRecords:Object[], languageSelected:string):Object{
    let language = {};
    for( var q of sqlRecords || [] ){
      try{
        let lang = JSON.parse(q['LANGUAGE']) as Object[];
        let trans = lang.find(d=>d['language']==languageSelected);
        Object.assign(language,{[q['ID'].toUpperCase()]:trans['text']});
      }catch(Exception){
        console.log("Problema de Traducao no campo "+q['ID']+". Erro:"+ Exception);
        Object.assign(language,{[q['ID'].toUpperCase()]:"LangError"});
      }
    }
    return language;
  }
}



const maskConfig: Partial<IConfig> = {
  validation: false,
};

/**
 * Stub de NzModalRef para componentes de diálogo usados FORA de um modal
 * (rotas como /changepassword e /flowaction, uso inline em outra tela ou
 * criação dinâmica pelo REGISTRY/menu). Substitui o antigo
 * `{ provide: MatDialogRef, useValue: {} }` do Material — a diferença é que
 * aqui os métodos são no-ops, então chamar close() fora de modal não estoura.
 *
 * Dentro de um NzModal o injector do próprio modal fornece a instância real,
 * que tem prioridade sobre este provider de módulo.
 *
 * IMPORTANTE: `updateConfig` precisa existir — as diretivas nzModalTitle e
 * nzModalFooter do NG-Zorro injetam NzModalRef como opcional e chamam
 * `updateConfig` sem verificar se é um modal de verdade.
 */
const NZ_MODAL_REF_FALLBACK = {
  close: () => {},
  destroy: () => {},
  triggerOk: () => {},
  triggerCancel: () => {},
  updateConfig: () => {},
  getContentComponent: () => null,
  getElement: () => null,
  afterClose: EMPTY,
  afterOpen: EMPTY
};


const RouterMktPlace: Routes = [
  {
    path: "changepassword",
    component: ChangePasswordFormComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "privacy",
    component:PrivacyComponent,
  },
  {
    path: "tempmenu",
    component: ShopeeAttributeComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "tempmenu3",
    component: WebhookLogComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "tempmenu2",
    component: MainDashboardComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "flowaction",
    component: FlowModalComponent,
    canActivate: [AuthGuard],
  },
  {
    path: "",
    component: SystemViewLayoutSelectorComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: "home",
        component: MainDashboardComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "agentbuilder",
        component: AgentBuilderListComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "ConfigJson",
        component: ConfigJsonComponent,
        canActivate: [AuthGuard],
      },
       {
        path: "JsonImporter",
        component: JsonImporterComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "authentication",
        component: ExternalAuthenticationComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "PushPedidos",
        component: PushPedidosComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "produtos",
        component: ProdutosComponent,
        data: { table_name: "Mktpproducts", entityDesc: "Produtos" },
        canActivate: [AuthGuard],
      },
      {
        path: "AmazonCreator",
        component: CriacaoProdutoFormComponent,
        data: { table_name: "Mktpproducts", entityDesc: "Produtos" },
        canActivate: [AuthGuard],
      },
      {
        path: "LogFiles",
        component: LogAplicacaoComponent,
        canActivate: [AuthGuard],
      },
      {
        // Construtor NOVO (Fase 4), carregado SOB DEMANDA. Quem abre o sistema
        // para conferir um pedido nao paga pelo construtor de telas.
        //
        // O AuthGuard fica aqui, na rota pai, e nao dentro do arquivo lazy:
        // guardar la dentro carregaria o construtor inteiro so para descobrir
        // que o usuario nao pode entrar.
        path: "screen-builder",
        canActivate: [AuthGuard],
        loadChildren: () =>
          import("./screen-builder/screen-builder.routes").then(
            (m) => m.SCREEN_BUILDER_ROUTES
          ),
      },
      {
        path: "screen-builder-legacy",
        component: BuilderScreenBetaComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "FormulaTester",
        component: FormulaTesterComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "formula-lab",
        component: FormulaLabComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "SimpleDatagridComponent",
        component: SimpleDatagridComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "ProductAmazon",
        component: NewProductAmazonComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "MeliDownload",
        component: MeliMlbDownloadComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "queryexecutor",
        component: QueryExecutorComponent,
        canActivate: [AuthGuard],
      },
      { path: "system", component: SystemComponent, canActivate: [AuthGuard] },
      {
        path: "anuncios",
        component: AnunciosComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "importacaodados",
        component: ImportacaoDadosComponent,
        canActivate: [AuthGuard],
      },
      {
        path: "AcessView",
        component: AcessViewComponent,
        canActivate: [AuthGuard],
        data: {
          table_name: "Mktpmenuacess",
          entityDesc: "Preferências do Sistema",
        },
      },
      {
        path:'WorkFlowCreator',
        component: WebhookCreatorComponent,
        canActivate: [AuthGuard]
      },
      {
        path:"MenuCreator",
        component:MenuCreatorComponent,
        canActivate: [AuthGuard]
      },
      {
        path:"translation",
        component:TranslateFormComponent,
        canActivate: [AuthGuard]
      },
      {
        // Tela antiga da conciliadora descontinuada: VAULT (conciliadora-c) e a
        // unica versao mantida. As URLs antigas redirecionam pra ela.
        path: "conciliadora-config",
        redirectTo: "conciliadora-c/config",
        pathMatch: "full"
      },
      {
        path: "conciliadora",
        redirectTo: "conciliadora-c/main",
        pathMatch: "full"
      },
      {
        // VAULT (novo layout da conciliadora) - acessivel so por URL, SEM menu.
        // Quando aprovado, basta apontar o menu pra ca. /conciliadora-c/main e /conciliadora-c/config
        path: "conciliadora-c",
        loadChildren: () => import('./conciliadora-redesign-c/conciliadora-redesign-c.module').then(m => m.ConciliadoraRedesignCModule),
        canActivate: [AuthGuard]
      },
      {
        // Tela SOMENTE-LEITURA de repasses Koncili para o cliente final.
        // Reusa HTTPService/UserAuth (tenant filtrado no KonciliRepasseService). Menu IDMENU='conciliacoes'.
        path: "conciliacoes",
        loadChildren: () => import('./conciliacoes-cliente/conciliacoes-cliente.module').then(m => m.ConciliacoesClienteModule),
        canActivate: [AuthGuard]
      },
      {
        // Separacao & Conferencia de Pedidos (picking/checking de marketplaces).
        // /separacao (fila mobile), /separacao/picking/:idwave, /separacao/conferencia/:idwave,
        // /separacao/config (desktop). Persistencia da operacao 100% via fluxos TemAPI.
        path: "separacao",
        loadChildren: () => import('./separacao-pedidos/separacao-pedidos.module').then(m => m.SeparacaoPedidosModule),
        canActivate: [AuthGuard]
      },
      {
        // Rota dedicada da CONFIGURACAO da separacao (IDMENU de 1 segmento p/
        // item de menu proprio). Redireciona para a rota filha do modulo lazy.
        path: "separacao-config",
        redirectTo: "separacao/config",
        pathMatch: "full"
      },
      // Chamados (service desk). Uma rota de UM segmento por tela, cada uma
      // com o seu AuthGuard — o padrao do resto do sistema.
      //
      // POR QUE NAO "chamados/meus": o AuthGuard le a permissao pelo caminho
      // da rota que ele guarda, e o cadastro de menu (MKTPMENUACESS) e por
      // item. Com rota filha, as tres telas apareciam para o guard como a
      // mesma rota "chamados", e liberar o item certo no cadastro nao surtia
      // efeito nenhum.
      //
      // Abrir chamado NAO tem rota — e um modal central, chamado do balao
      // flutuante e da tela de acompanhamento. O front so LE; quem escreve
      // chamado e mensagem e o TicketServices.
      {
        path: "chamados-mesa",
        loadChildren: () => import('./chamados/chamados.module').then(m => m.ChamadosMesaModule),
        canActivate: [AuthGuard]
      },
      {
        path: "chamados-meus",
        loadChildren: () => import('./chamados/chamados.module').then(m => m.ChamadosMeusModule),
        canActivate: [AuthGuard]
      },
      {
        path: "chamados-config",
        loadChildren: () => import('./chamados/chamados.module').then(m => m.ChamadosConfigModule),
        canActivate: [AuthGuard]
      }
    ],
  },

  {
    path: "",
    component: AuthenticationFormComponent,
    children: [
      {
        path: "auth",
        component: AuthenticationFormComponent,
      },
    ],
  },
];

@NgModule({ declarations: [AppComponent,PrintScreenAlertComponent],
    exports: [
      
    ],
    bootstrap: [AppComponent], 
    
    imports: [
        BrowserModule,
        DynamicFormsModule,
        BrowserAnimationsModule,
        LayoutModule,
        NgxDatatableModule,
        SharedModule,
        SystemViewModule,
        AuthModule,
        FormsModule,
        JsonEditorModule,
        BuilderScreenBetaModule,
        AgentBuilderModule,
        ConciliadoraModule,
        ConnectionBarComponent,
        QuillModule.forRoot(),
        TranslateModule.forRoot({
          loader: {
            provide: TranslateLoader, useClass: CustomTranslateLoader, deps: [HttpClient]
          }

        }),
        RouterModule.forRoot(RouterMktPlace, {
            preloadingStrategy: PreloadAllModules,
            //relativeLinkResolution: "legacy",
        }),
        LazyLoadImageModule,
        NgxMaskModule.forRoot(maskConfig),
        FontAwesomeModule], 
        providers: [
          LanguageServices,
         //{ provide: NZ_ICONS, useValue: [] },
        //{ provide: NZ_ICONS_PATCH, useValue: [] },
        //{ provide: NzIconService, useClass: GlobalIconeFallBackService },
        { provide: LOCALE_ID, useValue: 'pt-BR' },
        // Fallbacks para componentes de diálogo usados fora de modal — mesmo
        // papel dos antigos providers de MAT_DIALOG_DATA/MatDialogRef
        // (ver NZ_MODAL_REF_FALLBACK acima).
        { provide: NZ_MODAL_DATA, useValue: {} },
        { provide: NzModalRef, useValue: NZ_MODAL_REF_FALLBACK },
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
        },
        {
            provide: RouteReuseStrategy,
            useClass: CacheRouteReuseStrategy
        },
        NgxMaskPipe,
        provideHttpClient(withInterceptorsFromDi()),
        DynamicLoaderService,
        ConnectionLostBarService
    ] })
export class AppModule {}
